import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth, type User } from '../../../auth/AuthContext';
import { Modal } from '../../../components/Modal';
import { useErrorNotification } from '../../../hooks/useErrorNotification';
import {
  type AdminUser,
  type StatusMessage,
  type SubscriptionAssignment,
  type SubscriptionPlan,
  type SubscriptionStatusSummary,
} from '../types';
import { formatDateTime } from '../utils';
import { AddSubscriptionModal } from './AddSubscriptionModal';
import { AssignmentEndDateModal } from './AssignmentEndDateModal';

type UserDrawerProps = {
  user: AdminUser;
  onClose: () => void;
  onUserUpdated: (user: AdminUser) => void;
};

function statusBadgeClass(status: SubscriptionAssignment['status']) {
  switch (status) {
    case 'active':
      return 'bg-success';
    case 'scheduled':
      return 'bg-info';
    case 'expired':
      return 'bg-danger';
    case 'inactive':
    case 'plan_inactive':
      return 'bg-secondary';
    default:
      return 'bg-secondary';
  }
}

export function UserDrawer({ user, onClose, onUserUpdated }: UserDrawerProps) {
  const { authedRequest } = useAuth();
  const [email, setEmail] = useState(user.email);
  const [isActive, setIsActive] = useState(user.isActive);
  const [role, setRole] = useState<User['role']>(user.role);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatusSummary | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [updatingAssignmentId, setUpdatingAssignmentId] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingEndDate, setEditingEndDate] = useState<SubscriptionAssignment | null>(null);
  const [cancelingStripe, setCancelingStripe] = useState(false);

  useErrorNotification(status, { header: 'User update error' });

  const loadPlans = useCallback(async () => {
    setLoadingPlans(true);
    try {
      const data = await authedRequest<{ items: SubscriptionPlan[] }>('/api/admin/subscription-types');
      setPlans(data.items);
    } catch (err: any) {
      setStatus({ type: 'err', text: err?.message || 'Failed to load subscription plans.' });
    } finally {
      setLoadingPlans(false);
    }
  }, [authedRequest]);

  const loadSubscriptionStatus = useCallback(async () => {
    setLoadingSubscriptions(true);
    try {
      const data = await authedRequest<{ subscriptionStatus: SubscriptionStatusSummary }>(
        `/api/admin/users/${user.id}/subscriptions`
      );
      setSubscriptionStatus(data.subscriptionStatus);
    } catch (err: any) {
      setStatus({ type: 'err', text: err?.message || 'Failed to load subscriptions.' });
    } finally {
      setLoadingSubscriptions(false);
    }
  }, [authedRequest, user.id]);

  useEffect(() => {
    setEmail(user.email);
    setIsActive(user.isActive);
    setRole(user.role);
  }, [user]);

  useEffect(() => {
    loadPlans();
    loadSubscriptionStatus();
  }, [loadPlans, loadSubscriptionStatus]);

  const handleSaveUser = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setStatus({ type: 'err', text: 'Email cannot be empty.' });
      return;
    }

    setSaving(true);
    setStatus({ type: 'info', text: 'Saving user changes…' });
    try {
      const data = await authedRequest<{ user: AdminUser }>(`/api/admin/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ email: trimmedEmail, isActive, role }),
      });
      onUserUpdated(data.user);
      setStatus({ type: 'ok', text: 'User updated successfully.' });
    } catch (err: any) {
      setStatus({ type: 'err', text: err?.message || 'Unable to update user.' });
    } finally {
      setSaving(false);
    }
  };

  const handleAssignmentToggle = async (assignment: SubscriptionAssignment, desired: boolean) => {
    setUpdatingAssignmentId(assignment.id);
    setStatus({ type: 'info', text: 'Updating subscription assignment…' });
    try {
      const response = await authedRequest<{
        assignment: SubscriptionAssignment;
        subscriptionStatus: SubscriptionStatusSummary;
      }>(`/api/admin/user-subscriptions/${assignment.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: desired }),
      });
      setSubscriptionStatus(response.subscriptionStatus);
      setStatus({
        type: 'ok',
        text: desired ? 'Subscription reactivated.' : 'Subscription deactivated.',
      });
    } catch (err: any) {
      setStatus({ type: 'err', text: err?.message || 'Failed to update subscription assignment.' });
    } finally {
      setUpdatingAssignmentId(null);
    }
  };

  const handleAssignmentEndDate = async (assignment: SubscriptionAssignment, endDate: Date | null) => {
    setUpdatingAssignmentId(assignment.id);
    setStatus({ type: 'info', text: 'Updating end date…' });
    try {
      const payload: Record<string, unknown> = {
        end_at: endDate ? endDate.toISOString() : null,
      };
      const response = await authedRequest<{
        assignment: SubscriptionAssignment;
        subscriptionStatus: SubscriptionStatusSummary;
      }>(`/api/admin/user-subscriptions/${assignment.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      setSubscriptionStatus(response.subscriptionStatus);
      setStatus({ type: 'ok', text: 'Subscription end date updated.' });
      setEditingEndDate(null);
    } catch (err: any) {
      setStatus({ type: 'err', text: err?.message || 'Failed to update end date.' });
    } finally {
      setUpdatingAssignmentId(null);
    }
  };

  const handleAddSubscription = async (
    planId: string,
    startAt: Date | null,
    endAt: Date | null,
    reason: string
  ) => {
    setStatus({ type: 'info', text: 'Assigning subscription…' });
    try {
      const payload: Record<string, unknown> = {
        subscription_id: planId,
      };
      if (startAt) {
        payload.start_at = startAt.toISOString();
      }
      if (endAt) {
        payload.end_at = endAt.toISOString();
      }
      if (reason.trim()) {
        payload.added_reason = reason.trim();
      }

      const response = await authedRequest<{
        assignment: SubscriptionAssignment;
        subscriptionStatus: SubscriptionStatusSummary;
      }>(`/api/admin/users/${user.id}/subscriptions`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setSubscriptionStatus(response.subscriptionStatus);
      setStatus({ type: 'ok', text: 'Subscription assigned successfully.' });
      setAddModalOpen(false);
    } catch (err: any) {
      setStatus({ type: 'err', text: err?.message || 'Failed to assign subscription.' });
    }
  };

  const planOptions = useMemo(() => plans, [plans]);
  const hasStripeAssignment = useMemo(
    () =>
      subscriptionStatus?.assignments.some(
        (assignment) => assignment.addedVia === 'payment_webhook' && assignment.status === 'active'
      ) ?? false,
    [subscriptionStatus?.assignments]
  );

  const handleCancelStripe = async () => {
    setCancelingStripe(true);
    setStatus({ type: 'info', text: 'Submitting Stripe cancellation…' });
    try {
      await authedRequest(`/api/admin/billing/${user.id}/cancel`, {
        method: 'POST',
      });
      setStatus({ type: 'ok', text: 'Stripe subscription will cancel at period end.' });
      await loadSubscriptionStatus();
    } catch (err: any) {
      setStatus({ type: 'err', text: err?.message || 'Failed to cancel Stripe subscription.' });
    } finally {
      setCancelingStripe(false);
    }
  };

  return (
    <Modal title={`Manage ${user.email}`} onClose={onClose} size="xl">
      {status && status.type !== 'err' && (
        <div
          className={`alert alert-${status.type === 'ok' ? 'success' : 'info'} alert-dismissible fade show`}
          role="status"
        >
          {status.text}
          <button type="button" className="btn-close" onClick={() => setStatus(null)} aria-label="Dismiss" />
        </div>
      )}

      <section className="mb-4">
        <h4 className="h5 mb-3">Account details</h4>
        <form
          className="row g-3"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSaveUser();
          }}
        >
          <div className="col-12 col-md-6">
            <label className="form-label" htmlFor="userEmail">
              Email
            </label>
            <input
              id="userEmail"
              type="email"
              className="form-control"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label" htmlFor="userRole">
              Role
            </label>
            <select
              id="userRole"
              className="form-select"
              value={role}
              onChange={(event) => setRole(event.target.value as User['role'])}
            >
              <option value="standard">Standard</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="col-12">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="userActive"
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
              />
              <label className="form-check-label" htmlFor="userActive">
                Account is active
              </label>
            </div>
          </div>
          <div className="col-12">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </section>

      <section>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="h5 mb-0">Subscription assignments</h4>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => setAddModalOpen(true)}
            disabled={loadingPlans || loadingSubscriptions}
          >
            <i className="fas fa-plus me-1" aria-hidden="true" /> Add subscription grant
          </button>
        </div>
        {(loadingPlans || loadingSubscriptions) && (
          <div className="text-muted small mb-3">Loading subscription data…</div>
        )}
        {subscriptionStatus && (
          <div className={`alert alert-${subscriptionStatus.allowed ? 'success' : 'warning'} mb-3`} role="note">
            <p className="mb-1">{subscriptionStatus.summary}</p>
            {subscriptionStatus.nextExpiration && (
              <p className="mb-0">
                Next expiration: {formatDateTime(subscriptionStatus.nextExpiration)}
              </p>
            )}
            {subscriptionStatus.contactEmail && (
              <p className="mb-0">
                Contact support at <a href={`mailto:${subscriptionStatus.contactEmail}`}>{subscriptionStatus.contactEmail}</a>.
              </p>
            )}
          </div>
        )}

        {subscriptionStatus && hasStripeAssignment && !loadingSubscriptions && (
          <div className="mb-3">
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={handleCancelStripe}
              disabled={cancelingStripe}
            >
              {cancelingStripe ? 'Submitting…' : 'Cancel Stripe subscription at period end'}
            </button>
            <div className="form-text">
              Users keep access until the current billing period ends.
            </div>
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-sm table-striped">
            <thead className="table-light">
              <tr>
                <th>Plan</th>
                <th>Status</th>
                <th>Start</th>
                <th>End</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(subscriptionStatus?.assignments ?? []).map((assignment) => (
                <tr key={assignment.id}>
                  <td>
                    <div className="fw-semibold">{assignment.subscriptionName}</div>
                    <div className="text-muted text-uppercase small">{assignment.subscriptionCode}</div>
                  </td>
                  <td>
                    <span className={`badge ${statusBadgeClass(assignment.status)}`}>{assignment.status}</span>
                  </td>
                  <td>{formatDateTime(assignment.startAt)}</td>
                  <td>{assignment.endAt ? formatDateTime(assignment.endAt) : 'No expiry'}</td>
                  <td className="text-center">
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => handleAssignmentToggle(assignment, !assignment.isManuallyActive)}
                        disabled={updatingAssignmentId === assignment.id}
                      >
                        {assignment.isManuallyActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setEditingEndDate(assignment)}
                        disabled={updatingAssignmentId === assignment.id}
                      >
                        Edit end
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {(subscriptionStatus?.assignments?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    No subscription assignments yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {addModalOpen && subscriptionStatus && (
        <AddSubscriptionModal
          plans={planOptions}
          loadingPlans={loadingPlans}
          onCancel={() => setAddModalOpen(false)}
          onSubmit={handleAddSubscription}
        />
      )}

      {editingEndDate && (
        <AssignmentEndDateModal
          assignment={editingEndDate}
          onCancel={() => setEditingEndDate(null)}
          onSubmit={(date) => handleAssignmentEndDate(editingEndDate, date)}
          submitting={updatingAssignmentId === editingEndDate.id}
        />
      )}
    </Modal>
  );
}
